/**
 * EventRateOverlayContent — inner content of the Event Rate debug tool.
 *
 * Two sections:
 *   1. EVENTS — table of EventBus.getEventStats() polled every 250ms.
 *      Columns: name · emits/sec · handler count · sparkline (last 15 reads).
 *      Threshold breaches highlight red so double-fires jump out visually.
 *
 *   2. RENDERS — per-entity render counts from RenderCountTracker. Any
 *      entity with count > 1 in the most recent frame is a double-render
 *      (the user's stated lag hypothesis). Also shows running totals.
 *
 * Visibility owned by DebugLauncher / DebugHUDHost.
 */

import React, { useEffect, useState, useRef } from 'react';
import { EventBus } from '../../../../shared/EventBus';
import { RenderCountTracker } from '../../../diagnostics/RenderCountTracker';
import { thresholdFor } from '../../../diagnostics/eventRateThresholds';
import { useColyseusMessages } from '../../hooks/useColyseusMessages';
import { CopyButton } from '../CopyButton';
import type { EventRateSnapshot, TickHealthSnapshot } from '../../../../shared/diagnostics/TickHealthTypes';


const POLL_INTERVAL_MS = 250;
const SPARK_HISTORY = 15;
const SPARK_W = 60;
const SPARK_H = 14;

interface EventStat {
  name: string;
  emitsLastSec: number;
  lastHandlerCount: number;
  lastEmitMs: number;
}

function fmtAgo(ms: number, now: number): string {
  const d = Math.max(0, now - ms);
  if (d < 1000) return 'now';
  if (d < 60000) return `${Math.floor(d / 1000)}s`;
  return `${Math.floor(d / 60000)}m`;
}

function sparklinePath(values: number[], maxVal: number): string {
  if (values.length < 2) return '';
  const stepX = SPARK_W / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = SPARK_H - (v / Math.max(1, maxVal)) * SPARK_H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export const EventRateOverlayContent: React.FC = () => {
  const [stats, setStats] = useState<EventStat[]>([]);
  const [serverStats, setServerStats] = useState<EventStat[]>([]);
  const [serverSnapAt, setServerSnapAt] = useState<number>(0);
  const [serverScope, setServerScope] = useState<string | undefined>(undefined);
  const [renders, setRenders] = useState<Record<string, number>>({});
  const [totals, setTotals] = useState({ frameCount: 0, totalDoubleRenderFrames: 0, totalDoubleRenderEvents: 0 });
  const [tickHealth, setTickHealth] = useState<TickHealthSnapshot | null>(null);
  const [, setTick] = useState(0);
  const historiesRef = useRef<Map<string, number[]>>(new Map());

  // Phase B — subscribe to server broadcast. Server sends `netobs:eventRate`
  // every ~500ms via EventRateBroadcaster. Lets us render a SERVER column
  // alongside the local CLIENT column so DJ can see amplification ratios.
  useColyseusMessages({
    'netobs:eventRate': (snap: EventRateSnapshot) => {
      setServerStats(snap.events);
      setServerSnapAt(snap.timestamp);
      setServerScope(snap.scope);
    },
    'netobs:tick': (snap: TickHealthSnapshot) => {
      setTickHealth(snap);
    },
  });

  useEffect(() => {
    const poll = () => {
      const bus = EventBus.getInstance();
      const fresh = bus.getEventStats();
      setStats(fresh);

      // Maintain a rolling sparkline history per event name.
      const histories = historiesRef.current;
      const seen = new Set<string>();
      for (const s of fresh) {
        seen.add(s.name);
        let h = histories.get(s.name);
        if (!h) {
          h = [];
          histories.set(s.name, h);
        }
        h.push(s.emitsLastSec);
        if (h.length > SPARK_HISTORY) h.shift();
      }
      // Decay histories for events that didn't appear (push a 0).
      for (const [name, h] of histories.entries()) {
        if (!seen.has(name)) {
          h.push(0);
          if (h.length > SPARK_HISTORY) h.shift();
          if (h.every(v => v === 0)) histories.delete(name);
        }
      }

      setRenders(RenderCountTracker.lastSnapshot());
      setTotals(RenderCountTracker.getTotals());

      setTick(t => t + 1);
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const now = Date.now();
  const doubleRenders = Object.entries(renders).filter(([, n]) => n > 1);

  // Build a lookup so we can show server-side rate next to client-side rate.
  // Server only updates every ~500ms so older snapshots will be slightly stale —
  // surfaced via the "Xs ago" header below the table.
  const serverByName = new Map<string, EventStat>();
  for (const s of serverStats) serverByName.set(s.name, s);

  return (
    <div
      data-testid="event-rate-overlay"
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 10,
        lineHeight: 1.4,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#7af', marginBottom: 2, letterSpacing: '0.05em', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>EVENTS · last 1s ({stats.length} client / {serverStats.length} server)</span>
        {serverSnapAt > 0 && (
          <span
            data-testid="server-scope-tag"
            style={{ color: '#666', fontWeight: 400 }}
            title={
              serverScope === 'process-global'
                ? `Server EventBus is process-wide (aggregates all rooms). Phase G.5 scope.`
                : `age of latest server snapshot`
            }
          >
            srv {fmtAgo(serverSnapAt, now)}
            {serverScope === 'process-global' && (
              <span style={{ marginLeft: 4, color: '#fa5' }}>⚠ global</span>
            )}
          </span>
        )}
        <CopyButton getText={() => {
          const lines = [
            `EVENT RATE @ ${new Date().toISOString()}`,
            `client emits (${stats.length}):`,
            ...stats.slice(0, 30).map(s => `  ${s.name}: ${s.emitsLastSec}/s · ${s.lastHandlerCount} handlers`),
            `server emits (${serverStats.length}):`,
            ...serverStats.slice(0, 30).map(s => `  ${s.name}: ${s.emitsLastSec}/s`),
            `server scope: ${serverScope}`,
            `server snapshot age: ${fmtAgo(serverSnapAt, Date.now())}`,
          ];
          return lines.join('\n');
        }} />
      </div>
      {stats.length === 0 && (
        <div style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>no recent emits</div>
      )}
      {stats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto auto auto auto', columnGap: 6, alignItems: 'center', fontSize: 9 }}>
          {stats.map((s) => {
            const limit = thresholdFor(s.name);
            const over = s.emitsLastSec > limit;
            const hist = historiesRef.current.get(s.name) ?? [];
            const maxVal = Math.max(limit, ...hist);
            const srv = serverByName.get(s.name);
            // Amplification ratio: server emits per client emit. >1.5 or <0.5
            // is suspicious — server is reacting to each client message more
            // (or fewer) times than expected.
            const ratio = srv && s.emitsLastSec > 0 ? srv.emitsLastSec / s.emitsLastSec : null;
            const ampSuspect = ratio !== null && (ratio > 1.5 || (ratio > 0 && ratio < 0.5));
            return (
              <React.Fragment key={s.name}>
                <span
                  data-testid={`event-row-${s.name}`}
                  data-over={over ? 'true' : 'false'}
                  style={{
                    color: over ? '#ff7f3f' : '#bbb',
                    fontWeight: over ? 700 : 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={s.name}
                >
                  {s.name.length > 28 ? s.name.slice(0, 27) + '…' : s.name}
                </span>
                <span style={{ color: over ? '#ff7f3f' : '#ccc', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }} title="client emits/sec">
                  {s.emitsLastSec}/s
                </span>
                <span
                  data-testid={`event-server-${s.name}`}
                  data-amp={ampSuspect ? 'true' : 'false'}
                  style={{
                    color: ampSuspect ? '#ff7f3f' : srv ? '#9bd' : '#444',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                  }}
                  title={srv ? `server: ${srv.emitsLastSec}/s · ratio ${ratio?.toFixed(2)}×` : 'no server data'}
                >
                  {srv ? `${srv.emitsLastSec}` : '·'}
                </span>
                <span style={{ color: '#666', textAlign: 'right' }}>
                  /{limit}
                </span>
                <span style={{ color: s.lastHandlerCount > 1 ? '#fa5' : '#666', textAlign: 'right' }} title="handler count">
                  ×{s.lastHandlerCount}
                </span>
                <svg width={SPARK_W} height={SPARK_H} style={{ display: 'block' }}>
                  <polyline
                    fill="none"
                    stroke={over ? '#ff7f3f' : ampSuspect ? '#ff7f3f' : '#4ea3ff'}
                    strokeWidth={1.2}
                    points={sparklinePath(hist, maxVal)}
                  />
                </svg>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div style={{ color: '#7af', margin: '8px 0 2px', letterSpacing: '0.05em', fontWeight: 600 }}>
        RENDERS · last frame
      </div>
      <div style={{ fontSize: 9, color: '#888', marginBottom: 3 }}>
        frames: {totals.frameCount} ·{' '}
        <span style={{ color: totals.totalDoubleRenderFrames > 0 ? '#fa5' : '#888' }}>
          dup frames: {totals.totalDoubleRenderFrames}
        </span>{' '}
        · dup events: {totals.totalDoubleRenderEvents}
      </div>
      {doubleRenders.length === 0 ? (
        <div style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>
          no double-renders detected this frame
        </div>
      ) : (
        <div data-testid="render-doubles" style={{ fontSize: 9, color: '#fa5' }}>
          {doubleRenders.map(([key, n]) => (
            <div key={key}>
              {key}: <span style={{ fontWeight: 700 }}>×{n}</span>
            </div>
          ))}
        </div>
      )}

      {/* Phase H.6 — server tick budget breakdown. Mean / max for the
          current window + per-subsystem rollup. Lets DJ see "mobAI ate
          11ms of the 16.67ms budget this second" instead of just "tick
          health red." Source: netobs:tick broadcast from TickHealthService. */}
      {tickHealth && (
        <>
          <div style={{ color: '#7af', margin: '8px 0 2px', letterSpacing: '0.05em', fontWeight: 600 }}>
            TICK BUDGET · server
          </div>
          {(() => {
            const overBudget = tickHealth.maxMs > tickHealth.budgetMs;
            const meanPctOfBudget = (tickHealth.meanMs / tickHealth.budgetMs) * 100;
            return (
              <div style={{ fontSize: 9, color: '#888', marginBottom: 3 }}>
                mean: <span
                  data-testid="tick-mean"
                  style={{ color: meanPctOfBudget > 50 ? '#fa5' : '#aaa', fontWeight: 600 }}
                >{tickHealth.meanMs.toFixed(2)}ms</span>
                {' · max: '}
                <span style={{ color: overBudget ? '#ff7f3f' : '#aaa' }}>
                  {tickHealth.maxMs.toFixed(2)}ms
                </span>
                {' · budget: '}
                <span style={{ color: '#666' }}>{tickHealth.budgetMs.toFixed(2)}ms</span>
                {' · samples: '}
                <span style={{ color: '#666' }}>{tickHealth.sampleCount}</span>
              </div>
            );
          })()}
          {tickHealth.subsystems && Object.keys(tickHealth.subsystems).length > 0 && (
            <div data-testid="tick-subsystems" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', columnGap: 6, fontSize: 9, color: '#bbb' }}>
              {Object.entries(tickHealth.subsystems)
                .sort(([, a], [, b]) => b.meanMs - a.meanMs)
                .map(([name, sub]) => {
                  const pctOfBudget = (sub.meanMs / tickHealth.budgetMs) * 100;
                  const over30 = pctOfBudget > 30;
                  return (
                    <React.Fragment key={name}>
                      <span style={{ color: over30 ? '#fa5' : '#bbb' }}>{name}</span>
                      <span style={{ textAlign: 'right', color: over30 ? '#fa5' : '#aaa' }} title="mean ms">
                        {sub.meanMs.toFixed(2)}
                      </span>
                      <span style={{ textAlign: 'right', color: '#666' }} title="max ms">
                        ↑{sub.maxMs.toFixed(1)}
                      </span>
                    </React.Fragment>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* Reconciliation panel removed (2026-05-27) — client-owned movement
          eliminated server→client corrections. All stats are always zero. */}

      {/* Phase H.7 — payload size profiler. Reads NetworkTracer's
          per-message-type stats. Flags any type with avgBytes > 200
          (orange) or maxBytes > 500 (red). Catches accidentally-fat
          broadcasts like sending whole world snapshots when a diff
          would do. */}
      {(() => {
        const tracer = (window as any).nm?.tracer;
        if (!tracer) return null;
        type Row = { type: string; dir: 'in' | 'out'; count: number; avg: number; max: number };
        const rows: Row[] = [];
        const outMap = tracer.outboundMessageTypeStats as Map<string, { count: number; avgBytes: number; maxBytes?: number }>;
        const inMap = tracer.inboundMessageTypeStats as Map<string, { count: number; avgBytes: number; maxBytes?: number }>;
        if (outMap) {
          for (const [t, s] of outMap.entries()) rows.push({ type: t, dir: 'out', count: s.count, avg: s.avgBytes, max: s.maxBytes ?? s.avgBytes });
        }
        if (inMap) {
          for (const [t, s] of inMap.entries()) rows.push({ type: t, dir: 'in', count: s.count, avg: s.avgBytes, max: s.maxBytes ?? s.avgBytes });
        }
        // Sort by max desc; biggest payload offenders first.
        rows.sort((a, b) => b.max - a.max);
        const top = rows.slice(0, 8);
        if (top.length === 0) return null;
        return (
          <>
            <div style={{ color: '#7af', margin: '8px 0 2px', letterSpacing: '0.05em', fontWeight: 600 }}>
              PAYLOAD SIZES · session (top 8 by max bytes)
            </div>
            <div data-testid="payload-sizes" style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto auto auto', columnGap: 6, fontSize: 9, color: '#bbb' }}>
              {top.map((r) => {
                const overAvg = r.avg > 200;
                const overMax = r.max > 500;
                return (
                  <React.Fragment key={r.dir + r.type}>
                    <span style={{ color: r.dir === 'out' ? '#fa5' : '#5af' }}>
                      {r.dir === 'out' ? '↑' : '↓'}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.type}>
                      {r.type.length > 24 ? r.type.slice(0, 23) + '…' : r.type}
                    </span>
                    <span style={{ textAlign: 'right', color: '#666' }}>×{r.count}</span>
                    <span style={{ textAlign: 'right', color: overAvg ? '#fa5' : '#aaa' }} title="avg bytes">
                      avg {r.avg}
                    </span>
                    <span style={{ textAlign: 'right', color: overMax ? '#ff7f3f' : '#aaa' }} title="max bytes">
                      max {r.max}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        );
      })()}

      <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #2a2f3a', fontSize: 9, color: '#666' }}>
        polls every {POLL_INTERVAL_MS}ms · {fmtAgo(stats[0]?.lastEmitMs ?? now, now)} since last emit
      </div>
    </div>
  );
};
